import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { LogIn, LogOut, Pencil, Trash2, Camera } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const TZ = 'Asia/Dubai';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: TZ,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(',', '');
  } catch { return iso; }
};

const EventsTab = ({ branches, onClockOut }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 10);
  const [eventsForm, setEventsForm] = useState({ date_from: today, date_to: today });
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editEvent, setEditEvent] = useState(null);
  const [editForm, setEditForm] = useState({ type: 'in', timestamp: '', branch_id: '', note: '', employee_id: '' });
  const [selfieEvent, setSelfieEvent] = useState(null);
  const [selfieBlobUrl, setSelfieBlobUrl] = useState(null);

  const reloadEvents = () => {
    axios.get(`${API}/api/clock/events`, { headers, params: eventsForm })
      .then(r => setEvents(r.data || []));
  };

  // Staff list for the "reassign to correct employee" selector.
  useEffect(() => {
    axios.get(`${API}/api/clock/admin/pins`, { headers })
      .then(r => setStaff(r.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsForm]);

  useEffect(() => {
    if (!selfieEvent) {
      if (selfieBlobUrl) URL.revokeObjectURL(selfieBlobUrl);
      setSelfieBlobUrl(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/api/clock/admin/selfie/${selfieEvent.id}`, { headers, responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        setSelfieBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setSelfieBlobUrl('error'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfieEvent]);

  const openEditEvent = (ev) => {
    setEditEvent(ev);
    setEditForm({
      type: ev.type,
      timestamp: ev.timestamp?.slice(0, 16) || '',
      branch_id: ev.branch_id || '',
      note: ev.note || '',
      employee_id: ev.employee_id || '',
    });
  };

  const saveEvent = async () => {
    try {
      const res = await axios.put(`${API}/api/clock/events/${editEvent.id}`, {
        type: editForm.type,
        timestamp: editForm.timestamp + ':00Z',
        branch_id: editForm.branch_id || null,
        note: editForm.note,
        employee_id: editForm.employee_id || editEvent.employee_id,
      }, { headers });
      toast({ title: res?.data?.reassigned ? 'Punch reassigned & updated' : 'Event updated' });
      setEditEvent(null);
      reloadEvents();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this clock event?')) return;
    try {
      await axios.delete(`${API}/api/clock/events/${id}`, { headers });
      reloadEvents();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  // Determine which IN events are still open (no later OUT for same employee in this list)
  const buildOpenInSet = () => {
    const sortedByEmp = {};
    events.forEach(ev => { (sortedByEmp[ev.employee_id] ||= []).push(ev); });
    const openInSet = new Set();
    Object.values(sortedByEmp).forEach(list => {
      list.sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      let openIn = null;
      list.forEach(ev => {
        if (ev.type === 'in') openIn = ev.id;
        else if (ev.type === 'out' && openIn) openIn = null;
      });
      if (openIn) openInSet.add(openIn);
    });
    return openInSet;
  };

  return (
    <div className="space-y-3" data-testid="events-tab">
      <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
        <div><Label className="text-xs">From</Label>
          <Input type="date" value={eventsForm.date_from} onChange={e => setEventsForm(f => ({ ...f, date_from: e.target.value }))} /></div>
        <div><Label className="text-xs">To</Label>
          <Input type="date" value={eventsForm.date_to} onChange={e => setEventsForm(f => ({ ...f, date_to: e.target.value }))} /></div>
        <p className="ml-auto text-xs text-gray-500">{events.length} events</p>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="text-left p-3">Time</th>
              <th className="text-left p-3">Employee</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Station</th>
              <th className="text-left p-3">Note</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-gray-500">No events</td></tr> :
              (() => {
                const openInSet = buildOpenInSet();
                return events.map(e => (
                <tr key={e.id} className="border-t hover:bg-gray-50" data-testid={`event-row-${e.id}`}>
                  <td className="p-3 font-mono text-xs">{fmtDateTime(e.timestamp)}</td>
                  <td className="p-3">{e.employee_name} <span className="text-xs text-gray-400">· {e.employee_code}</span></td>
                  <td className="p-3">
                    {e.type === 'in' ?
                      <Badge className="bg-emerald-100 text-emerald-700"><LogIn className="h-3 w-3 mr-1" /> IN</Badge> :
                      <Badge className="bg-sky-100 text-sky-700"><LogOut className="h-3 w-3 mr-1" /> OUT</Badge>}
                    {e.manual && <Badge variant="outline" className="ml-1 text-[10px]">Manual</Badge>}
                    {openInSet.has(e.id) && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">Open</Badge>}
                  </td>
                  <td className="p-3 text-xs">
                    {e.branch_name || '—'}
                    {e.flag_ip_mismatch && <Badge variant="outline" className="ml-1 text-[10px] text-rose-700 border-rose-300">Off-network</Badge>}
                    {e.flag_no_gps && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">No GPS</Badge>}
                    {e.flag_no_geofence && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">No geo-pin</Badge>}
                    {e.flag_face_mismatch && <Badge variant="outline" className="ml-1 text-[10px] text-rose-700 border-rose-300">Face mismatch</Badge>}
                    {e.flag_no_selfie && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">No selfie</Badge>}
                    {e.flag_no_reference_photo && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">No ref photo</Badge>}
                    {e.distance_m != null && (
                      <span className="ml-1 text-[10px] text-gray-500">· {Math.round(e.distance_m)}m</span>
                    )}
                    {e.face_match_score != null && (
                      <span className={`ml-1 text-[10px] ${e.flag_face_mismatch ? 'text-rose-600' : 'text-emerald-600'}`}>· face {Math.round(e.face_match_score * 100)}%</span>
                    )}
                    {e.selfie_path && (
                      <button
                        onClick={() => setSelfieEvent(e)}
                        className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-violet-600 underline"
                        data-testid={`view-selfie-${e.id}`}
                      >
                        <Camera className="h-3 w-3" /> selfie
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-xs text-gray-500">{e.note || '—'}</td>
                  <td className="p-3 text-right">
                    {openInSet.has(e.id) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 mr-1 text-[11px] text-sky-700 border-sky-300"
                        onClick={() => onClockOut({
                          event_id: e.id,
                          employee_id: e.employee_id,
                          employee_name: e.employee_name,
                          employee_code: e.employee_code,
                          in_at: e.timestamp,
                        })}
                        data-testid={`event-clockout-${e.id}`}
                        title="Manually clock out this open session"
                      >
                        <LogOut className="h-3 w-3 mr-1" /> Clock out
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEditEvent(e)} data-testid={`edit-event-${e.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteEvent(e.id)} className="text-rose-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ));
              })()}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Selfie viewer dialog */}
      <Dialog open={!!selfieEvent} onOpenChange={(o) => { if (!o) setSelfieEvent(null); }}>
        <DialogContent className="max-w-lg" data-testid="selfie-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-violet-600" />
              Selfie — {selfieEvent?.employee_name}
            </DialogTitle>
          </DialogHeader>
          {selfieEvent && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded p-3 text-xs space-y-1">
                <p><span className="text-gray-500">Time:</span> {fmtDateTime(selfieEvent.timestamp)}</p>
                <p><span className="text-gray-500">Station:</span> {selfieEvent.branch_name || '—'}</p>
                <p><span className="text-gray-500">Type:</span> {selfieEvent.type === 'in' ? 'IN' : 'OUT'}</p>
                {selfieEvent.face_match_score != null && (
                  <p>
                    <span className="text-gray-500">Face match score:</span>{' '}
                    <span className={selfieEvent.flag_face_mismatch ? 'text-rose-600 font-bold' : 'text-emerald-600 font-bold'}>
                      {Math.round(selfieEvent.face_match_score * 100)}%
                      {selfieEvent.flag_face_mismatch ? ' — mismatch' : ' — matched'}
                    </span>
                  </p>
                )}
                {selfieEvent.flag_no_reference_photo && (
                  <p className="text-amber-700">⚠ Employee has no profile photo for comparison</p>
                )}
              </div>
              <div className="text-center">
                <p className="text-[11px] uppercase text-gray-500 mb-1">Captured Selfie</p>
                {!selfieBlobUrl && <div className="aspect-square bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">Loading…</div>}
                {selfieBlobUrl === 'error' && <div className="aspect-square bg-rose-50 rounded flex items-center justify-center text-xs text-rose-600 p-3 text-center">Selfie expired or unavailable (7-day retention)</div>}
                {selfieBlobUrl && selfieBlobUrl !== 'error' && (
                  <img src={selfieBlobUrl} alt="Selfie" className="max-h-[400px] mx-auto object-contain rounded" data-testid="selfie-image" />
                )}
              </div>
              <p className="text-[10px] text-gray-400 text-center">
                Selfies are auto-deleted from records 7 days after the clock event.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit event dialog */}
      <Dialog open={!!editEvent} onOpenChange={(o) => { if (!o) setEditEvent(null); }}>
        <DialogContent data-testid="edit-event-dialog">
          <DialogHeader><DialogTitle>Edit Clock Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              {editEvent?.employee_name} · {editEvent?.employee_code}
              {editEvent?.reassigned && editEvent?.reassigned_from_code && (
                <span className="ml-1 text-violet-600"> (reassigned from {editEvent.reassigned_from_code})</span>
              )}
            </p>

            {/* Reassign to correct employee — fixes mis-typed employee ID punches */}
            <div>
              <Label>Employee</Label>
              <select
                value={editForm.employee_id || ''}
                onChange={e => setEditForm(f => ({ ...f, employee_id: e.target.value }))}
                className="w-full px-2 py-1.5 border rounded-md text-sm"
                data-testid="edit-employee"
              >
                {staff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.employee_code} — {s.name}{s.effectively_active === false ? ' (inactive)' : ''}
                  </option>
                ))}
              </select>
              {editForm.employee_id && editForm.employee_id !== editEvent?.employee_id && (
                <p className="text-[11px] text-violet-700 bg-violet-50 rounded px-2 py-1 mt-1" data-testid="reassign-warning">
                  This punch & its selfie will move to the selected employee and fix their attendance. The original
                  ({editEvent?.employee_code}) will no longer show this punch.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Type</Label>
                <select value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))} className="w-full px-2 py-1.5 border rounded-md text-sm">
                  <option value="in">IN</option>
                  <option value="out">OUT</option>
                </select>
              </div>
              <div>
                <Label>Branch</Label>
                <select value={editForm.branch_id || ''} onChange={e => setEditForm(f => ({ ...f, branch_id: e.target.value }))} className="w-full px-2 py-1.5 border rounded-md text-sm">
                  <option value="">—</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Timestamp</Label>
              <Input type="datetime-local" value={editForm.timestamp} onChange={e => setEditForm(f => ({ ...f, timestamp: e.target.value }))} data-testid="edit-timestamp" />
            </div>
            <div>
              <Label>Note</Label>
              <Input value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} placeholder="Reason for edit" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditEvent(null)}>Cancel</Button>
              <Button onClick={saveEvent} className="bg-violet-600 hover:bg-violet-700" data-testid="save-event-edit">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsTab;
